/**
 * Article typography — ONE definition, applied to both the editor's canvas and the
 * read-only render.
 *
 * Both surfaces share it because the guarantee is that a reader sees exactly what
 * the author produced. Two class lists would drift, and the drift would be
 * invisible until someone compared the composer with the Brand details tab.
 *
 * Written as element-scoped arbitrary variants rather than `prose`: this project
 * has Tailwind v4 WITHOUT `@tailwindcss/typography`, so `prose` and `prose-sm` are
 * no-op classes — they matched nothing, and Tailwind's preflight resets a heading
 * to `font-size: inherit`, which is why an `<h1>` rendered identically to a
 * paragraph. Adding the plugin would also bring its own scale and colours, which
 * would then have to be overridden back to the `ds-*` tokens element by element.
 *
 * Every value here is a design token.
 */

const HEADINGS = [
	// The display family and weights the app already uses for headings (`PageHeader`),
	// so an article's sections read as part of the same product.
	'[&_h1]:font-ds-display [&_h1]:text-ds-3xl [&_h1]:font-semibold [&_h1]:leading-tight [&_h1]:text-ds-text',
	'[&_h1]:mt-10 [&_h1]:mb-3 [&_h1:first-child]:mt-0',
	'[&_h2]:font-ds-display [&_h2]:text-ds-2xl [&_h2]:font-semibold [&_h2]:leading-snug [&_h2]:text-ds-text',
	'[&_h2]:mt-8 [&_h2]:mb-2 [&_h2:first-child]:mt-0',
	'[&_h3]:text-ds-lg [&_h3]:font-semibold [&_h3]:text-ds-text [&_h3]:mt-6 [&_h3]:mb-2',
].join(' ')

const BLOCKS = [
	'[&_p]:my-3 [&_p]:leading-relaxed',
	'[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6',
	'[&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6',
	'[&_li]:my-1',
	'[&_blockquote]:my-4 [&_blockquote]:border-l-2 [&_blockquote]:border-ds-border [&_blockquote]:pl-4 [&_blockquote]:text-ds-text-muted',
	'[&_pre]:my-4 [&_pre]:overflow-x-auto [&_pre]:rounded-ds-md [&_pre]:bg-ds-surface [&_pre]:p-3 [&_pre]:font-ds-mono [&_pre]:text-ds-sm',
	'[&_code]:font-ds-mono [&_code]:text-ds-sm',
	'[&_hr]:my-8 [&_hr]:border-ds-border',
	// An image is inline content, so it takes its vertical rhythm from the paragraph
	// holding it. Vertical margins of its own would push the line box of any text it
	// shares that paragraph with.
	'[&_img]:max-w-full [&_img]:rounded-ds-md [&_img]:align-middle',
	'[&_a]:text-ds-primary [&_a]:underline',
	'[&_strong]:font-semibold',
].join(' ')

/**
 * The shared article body styling.
 *
 * Prose only — the palette block is NOT styled here. Its swatches are rendered by
 * `PaletteMatrix` on both surfaces instead, because styling the raw markup would
 * put each hex code inside its own swatch, on top of an arbitrary brand colour,
 * where legibility could only be rescued with a hardcoded contrast colour. The
 * component puts the code beneath the swatch and needs no such trick.
 */
export const ARTICLE_PROSE_CLASS = `${HEADINGS} ${BLOCKS}`
